import { imageDimensionsFromHeader } from "./image-header-dimensions";

export const getAspectRatio = (url: string): Promise<number> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img.width / img.height));
    img.addEventListener("error", reject);
    img.src = url;
  });

// Enough of the file to cover the header of the formats we can measure; JPEG frame headers sit
// after any EXIF thumbnail.
const HEADER_BYTES = 64 * 1024;

const readHeader = async (body: ReadableStream<Uint8Array>): Promise<Uint8Array> => {
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let read = 0;
  while (read < HEADER_BYTES) {
    // oxlint-disable-next-line no-await-in-loop -- sequential stream reads
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    read += value.length;
  }
  await reader.cancel();
  if (chunks.length === 1) return chunks[0]!;
  const header = new Uint8Array(read);
  let offset = 0;
  for (const chunk of chunks) {
    header.set(chunk, offset);
    offset += chunk.length;
  }
  return header;
};

// Our own images have to be fetched over a service binding rather than back out through the edge,
// so the caller supplies the fetch to use.
export type ImageFetch = (url: string, init?: RequestInit) => Promise<Response>;

// getAspectRatio needs a DOM, so this is the equivalent for the api worker: read the file header
// and take the dimensions from it. Undefined when the image cannot be fetched or measured, so
// callers keep their fallback ratio.
export const fetchAspectRatio = async (
  url: string,
  fetchImage: ImageFetch,
): Promise<number | undefined> => {
  const response = await fetchImage(url, { headers: { Range: `bytes=0-${HEADER_BYTES - 1}` } });
  if (!response.ok || !response.body) return undefined;
  const dimensions = imageDimensionsFromHeader(await readHeader(response.body));
  return dimensions && dimensions.height > 0 ? dimensions.width / dimensions.height : undefined;
};
